import React from 'react';
import { loadFont } from '../content-editor/DashboardParts';

// Single source of truth for site-level design tokens. Editor canvas and
// Live Preview both resolve through here so fonts/colours/radius never diverge.
export const DEFAULT_SWATCHES = ['#FAFAFA', '#E5DDFF', '#A78BFA', '#8B5CF6', '#0E0E23'];

export function resolveSiteTokens(theme = {}, settings = {}) {
  const t = theme || {};
  const s = settings || {};
  const palette = (Array.isArray(t.swatches) && t.swatches.length >= 5) ? t.swatches
    : (Array.isArray(s.palette_swatches) && s.palette_swatches.length >= 5) ? s.palette_swatches
    : DEFAULT_SWATCHES;
  const headingFont = t.heading_font || t.fonts || s.font_family || 'Syne';
  const bodyFont = t.body_font || 'Inter';
  const radius = t.button_radius != null ? t.button_radius
    : t.button_style === 'square' ? 0
    : t.button_style === 'rounded' ? 12
    : t.button_style === 'pill' ? 999
    : 8;
  return { palette, headingFont, bodyFont, radius, bg: palette[0], ink: palette[4] };
}

// The website surface itself — identical wrapper in Edit and Preview.
export function SiteSurface({ tokens, className = '', style, children, ...rest }) {
  React.useEffect(() => { loadFont(tokens.headingFont); loadFont(tokens.bodyFont); }, [tokens.headingFont, tokens.bodyFont]);
  return (
    <div
      className={`wb-site-surface bg-white ${className}`}
      style={{ color: tokens.ink, fontFamily: `'${tokens.bodyFont}', sans-serif`, ...(style || {}) }}
      {...rest}
    >
      {children}
    </div>
  );
}

let _sbw = null;
const scrollbarWidth = () => {
  if (_sbw != null) return _sbw;
  // Mirrors the editor canvas scroller (overflow-y:auto + stable gutter).
  const d = document.createElement('div');
  d.style.cssText = 'position:fixed;top:0;left:0;width:100px;height:100px;overflow-y:auto;scrollbar-gutter:stable;visibility:hidden;';
  document.body.appendChild(d);
  _sbw = d.offsetWidth - d.clientWidth;
  document.body.removeChild(d);
  return _sbw;
};

// Width of the full-screen edit canvas (viewport minus its reserved scrollbar
// gutter). Live Preview renders at exactly this width, then scales to fit.
export const getSiteViewportWidth = () => window.innerWidth - scrollbarWidth();

// Prefix every selector of vendor custom CSS with `scope` so it only styles
// the website surface (never the builder chrome) — same rules in Edit + Preview.
export function scopeCss(css, scope = '.wb-site-surface') {
  let sheet;
  try { sheet = new CSSStyleSheet(); sheet.replaceSync(css); } catch { return ''; }
  const walk = (rules) => Array.from(rules).map((r) => {
    if (r.selectorText != null && r.style) {
      const sel = r.selectorText.split(',').map((p) => {
        const x = p.trim();
        if (/^(html|body|:root)$/i.test(x)) return scope;
        return `${scope} ${x.replace(/^(html|body|:root)\s+/i, '')}`;
      }).join(', ');
      return `${sel} { ${r.style.cssText} }`;
    }
    if (r.cssRules && r.conditionText != null) {
      const kw = r.constructor.name === 'CSSSupportsRule' ? '@supports' : '@media';
      return `${kw} ${r.conditionText} { ${walk(r.cssRules)} }`;
    }
    return r.cssText;
  }).join('\n');
  return walk(sheet.cssRules);
}
